/**
 * Pure helpers for Settings deep-links after OAuth / gear open.
 */

import type { SettingsSectionId, SettingsSourceId } from './SettingsView';

const SETTINGS_SECTIONS = new Set<SettingsSectionId>([
  'providers',
  'sources',
  'slack',
  'jira',
  'github',
  'gmail',
  'openai-codex',
  'gemini',
  'opencode-go',
  'ai',
  'alerts',
  'appearance',
  'shortcuts',
  'changelog',
  'people',
]);

export function parseSettingsSectionParam(
  raw: string | null | undefined,
): SettingsSectionId | null {
  if (!raw) return null;
  return SETTINGS_SECTIONS.has(raw as SettingsSectionId) ? (raw as SettingsSectionId) : null;
}

export type JiraOauthReturnStatus = 'connected' | 'oauth_error' | null;

export function parseJiraOauthReturnParam(raw: string | null | undefined): JiraOauthReturnStatus {
  if (raw === 'connected' || raw === 'oauth_error') return raw;
  return null;
}

export function jiraOauthReturnMessage(status: JiraOauthReturnStatus): string | null {
  if (status === 'connected') {
    return 'Conexión OAuth con Atlassian completada. Usa «Probar conexión» para verificar la identidad.';
  }
  if (status === 'oauth_error') {
    return 'No se pudo completar OAuth con Atlassian. Reintenta o usa el token de API manual.';
  }
  return null;
}

/** True when the SPA should auto-open Settings from the current URL. */
export function shouldOpenSettingsFromSearch(search: string): boolean {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return parseSettingsSectionParam(params.get('settings')) != null;
}

const PROVIDER_SOURCES = new Set<SettingsSourceId>([
  'slack',
  'jira',
  'github',
  'openai-codex',
  'gemini',
  'opencode-go',
]);

export type ProviderOauthReturnStatus = 'connected' | 'error';
export type ProviderOauthFailureReason =
  | 'access_denied'
  | 'account_access'
  | 'state'
  | 'credentials'
  | 'exchange'
  | 'identity'
  | 'persistence'
  | 'encryption'
  | 'configuration'
  | 'unknown';

export interface ProviderOauthReturn {
  provider: SettingsSourceId;
  status: ProviderOauthReturnStatus;
  reason: ProviderOauthFailureReason | null;
}

export interface SettingsNavigation {
  section: SettingsSectionId;
  source: SettingsSourceId | null;
  oauthReturn: ProviderOauthReturn | null;
}

function parseSettingsSourceParam(raw: string | null | undefined): SettingsSourceId | null {
  if (!raw) return null;
  return PROVIDER_SOURCES.has(raw as SettingsSourceId) ? (raw as SettingsSourceId) : null;
}

export function parseProviderOauthReturn(
  params: URLSearchParams,
  fallbackProvider: SettingsSourceId | null = null,
): ProviderOauthReturn | null {
  const provider = parseSettingsSourceParam(params.get('provider')) ?? fallbackProvider;
  const status = params.get('oauth');
  if (!provider || (status !== 'connected' && status !== 'error')) return null;
  const rawReason = params.get('oauth_reason');
  const reasons = new Set<ProviderOauthFailureReason>([
    'access_denied',
    'account_access',
    'state',
    'credentials',
    'exchange',
    'identity',
    'persistence',
    'encryption',
    'configuration',
    'unknown',
  ]);
  const reason =
    rawReason && reasons.has(rawReason as ProviderOauthFailureReason)
      ? (rawReason as ProviderOauthFailureReason)
      : null;
  return { provider, status, reason };
}

export function parseSettingsNavigation(
  search: string,
  fallbackSection: SettingsSectionId = 'providers',
): SettingsNavigation {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const parsedSection = parseSettingsSectionParam(params.get('settings')) ?? fallbackSection;
  const directSource = parseSettingsSourceParam(parsedSection);
  const querySource = parseSettingsSourceParam(params.get('provider'));
  const source = querySource ?? directSource;
  return {
    section: source ? 'providers' : parsedSection,
    source,
    oauthReturn: parseProviderOauthReturn(params, source),
  };
}

export function providerOauthReturnMessage(value: ProviderOauthReturn): string {
  const label =
    value.provider === 'openai-codex'
      ? 'OpenAI / Codex'
      : value.provider === 'opencode-go'
        ? 'OpenCode Go'
        : value.provider.charAt(0).toUpperCase() + value.provider.slice(1);
  if (value.status === 'connected') {
    return `Conexión OAuth con ${label} completada y verificada contra el servicio.`;
  }
  if (value.reason === 'account_access') {
    return 'La cuenta de Atlassian seleccionada no puede acceder a un sitio Jira. Reintenta con la cuenta que tiene acceso al sitio de trabajo.';
  }
  if (value.reason === 'access_denied') {
    return `Cancelaste o denegaste la autorización de ${label}. Reintenta cuando quieras conectarlo.`;
  }
  if (value.reason === 'credentials') {
    return `${label} rechazó la configuración OAuth del servidor. La conexión requiere revisión administrativa.`;
  }
  if (value.reason === 'configuration') {
    return `OAuth con ${label} no está configurado en el servidor. Solicita una revisión administrativa antes de reintentar.`;
  }
  if (value.reason === 'identity') {
    return `${label} entregó un token, pero Zunelo no pudo verificar la identidad de la cuenta.`;
  }
  if (value.reason === 'persistence') {
    return `Zunelo verificó ${label}, pero no pudo guardar la conexión. Reintenta.`;
  }
  if (value.reason === 'encryption') {
    return `Zunelo no puede guardar ${label} porque el almacenamiento seguro del servidor requiere revisión administrativa.`;
  }
  if (value.reason === 'state') {
    return `El retorno OAuth de ${label} venció o no es válido. Inicia la conexión de nuevo.`;
  }
  if (value.reason === 'exchange') {
    return `${label} no pudo completar el intercambio OAuth. Reintenta; si persiste, revisa la aplicación del proveedor.`;
  }
  return `No se pudo completar OAuth con ${label}. Reintenta la conexión.`;
}

/**
 * Sections that are really a provider card at the Proveedores root, not a
 * nav entry of their own (the modal has no provider detail screen).
 */
const PROVIDER_SECTIONS = new Set<SettingsSectionId>([
  'slack',
  'jira',
  'github',
  'gmail',
  'openai-codex',
  'gemini',
  'opencode-go',
]);

export function isConnectableProviderSection(section: SettingsSectionId): boolean {
  return PROVIDER_SECTIONS.has(section);
}

export const OPEN_SETTINGS_EVENT = 'zunelo:open-settings';

/** Ask the layout chrome to open Settings on a given section (see `useMainLayoutChrome`). */
export function openSettingsSection(section: SettingsSectionId): void {
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT, { detail: { section } }));
}
